import React, { useMemo } from 'react';
import { App as AntApp, ConfigProvider, theme } from 'antd';
import App, { AppProviders } from './App';
import { useStorePrimitive } from './hooks/useStore';

const darkThemeTokens = {
  // Background hierarchy (creates spatial depth)
  colorBgLayout: '#0f0f11',
  colorBgContainer: '#18181b',
  colorBgElevated: '#27272a',

  // Primary brand color
  colorPrimary: '#3b82f6',
  colorPrimaryHover: '#60a5fa',
  colorPrimaryActive: '#2563eb',
  colorPrimaryBg: '#172554',
  colorPrimaryBgHover: '#1e3a5f',

  // Semantic colors
  colorSuccess: '#22c55e',
  colorWarning: '#f59e0b',
  colorError: '#ef4444',
  colorInfo: '#3b82f6',

  // Text (avoid pure white for comfort)
  colorTextBase: '#f4f4f5',
  colorText: '#e4e4e7',
  colorTextSecondary: '#a1a1aa',
  colorTextTertiary: '#71717a',
  colorTextQuaternary: '#52525b',

  // Borders
  colorBorder: '#3f3f46',
  colorBorderSecondary: '#27272a',

  // Fill states
  colorFill: '#3f3f46',
  colorFillSecondary: '#27272a',
  colorFillTertiary: '#18181b',
  colorFillQuaternary: '#18181b',

  // Shadows for elevation
  boxShadow: '0 1px 2px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2)',
  boxShadowSecondary: '0 2px 8px rgba(0,0,0,0.4), 0 12px 32px rgba(0,0,0,0.25)',

  // Typography
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', 'Segoe UI', Roboto, sans-serif",
  fontSize: 14,
  fontWeightStrong: 600,
  lineHeight: 1.6,

  // Border radius (unified)
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 6,

  // Links
  colorLink: '#60a5fa',
  colorLinkHover: '#93c5fd',
};

const lightThemeTokens = {
  colorBgLayout: '#f8f9fa',
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
  colorPrimary: '#2563eb',
  colorText: '#18181b',
  colorTextSecondary: '#52525b',
  colorBorder: '#e4e4e7',
  colorBorderSecondary: '#f4f4f5',
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 6,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', 'Segoe UI', Roboto, sans-serif",
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
  boxShadowSecondary: '0 4px 16px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.06)',
};

const AppWithTheme = () => {
  const currentTheme = useStorePrimitive(state => state.settings?.theme);
  const isDark = currentTheme === 'dark';

  // Create a container for the messages only once.
  const messageContainer = useMemo(() => {
    const container = document.createElement('div');
    container.className = 'custom-message-container';
    document.body.appendChild(container);
    return container;
  }, []);

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        cssVar: true,
        token: isDark ? darkThemeTokens : lightThemeTokens,
        components: {
          Card: {
            colorBorderSecondary: isDark ? '#27272a' : '#e4e4e7',
          },
          Table: {
            colorFillAlter: isDark ? '#1c1c1f' : '#fafafa',
          },
          Button: {
            primaryShadow: '0 2px 4px rgba(59,130,246,0.3)',
          },
          Modal: {
            contentBg: isDark ? '#1f1f23' : '#ffffff',
            headerBg: isDark ? '#1f1f23' : '#ffffff',
          },
          Tabs: {
            inkBarColor: isDark ? '#3b82f6' : '#2563eb',
            itemSelectedColor: isDark ? '#f4f4f5' : '#18181b',
            itemHoverColor: isDark ? '#e4e4e7' : '#3f3f46',
          },
          Input: {
            colorBgContainer: isDark ? '#18181b' : '#ffffff',
            activeBorderColor: isDark ? '#3b82f6' : '#2563eb',
          },
        },
      }}
    >
      <AntApp
        message={{
          getContainer: () => messageContainer,
        }}
      >
        <AppProviders>
          <App />
        </AppProviders>
      </AntApp>
    </ConfigProvider>
  );
};

export default AppWithTheme;